import React, { useEffect, useRef, useCallback, useState } from 'react';
import 'litegraph.js/css/litegraph.css';
import { LGraph, LGraphCanvas, LiteGraph } from 'litegraph.js';

const STORAGE_KEY = 'litegraph-nodes';

const LiteGraphComponent = () => {
    const [isSaveEnabled, setIsSaveEnabled] = useState(false); // Estado para activar/desactivar el guardado
    const canvasRef = useRef(null);
    const graphRef = useRef(null);
    const canvasInstanceRef = useRef(null);

    // Función para guardar el estado del grafo con posiciones explícitas
    const saveGraphState = useCallback(() => {
        if (!graphRef.current || !isSaveEnabled) return; // Verifica si el guardado está habilitado
        
        try {
            const graph = graphRef.current;
            const graphData = graph.serialize();
            
            // Guardar explícitamente la posición y estado de cada nodo
            graphData.nodes = graphData.nodes.map(node => {
                const originalNode = graph.getNodeById(node.id);
                if (originalNode) {
                    node.pos = [
                        parseFloat(originalNode.pos[0]),
                        parseFloat(originalNode.pos[1])
                    ];
                    node.title = originalNode.title;
                    node.properties = {
                        ...node.properties,
                        title: originalNode.title,
                        position: {
                            x: originalNode.pos[0],
                            y: originalNode.pos[1]
                        }
                    };
                }
                return node;
            });

            const fullState = {
                graphData,
                lastSaved: new Date().toISOString(),
                version: '1.0'
            };

            localStorage.setItem(STORAGE_KEY, JSON.stringify(fullState));
            console.log('Estado guardado con posiciones:', graphData.nodes.map(n => ({ id: n.id, pos: n.pos })));
        } catch (error) {
            console.error('Error saving graph state:', error);
        }
    }, [isSaveEnabled]);

    const loadGraphState = useCallback(() => {
        if (!graphRef.current) return;

        try {
            const savedData = localStorage.getItem(STORAGE_KEY);
            if (savedData) {
                const { graphData } = JSON.parse(savedData);

                graphRef.current.configure(graphData);

                graphData.nodes.forEach(nodeData => {
                    const node = graphRef.current.getNodeById(nodeData.id);
                    if (node) {
                        if (nodeData.pos && Array.isArray(nodeData.pos)) {
                            node.pos[0] = parseFloat(nodeData.pos[0]);
                            node.pos[1] = parseFloat(nodeData.pos[1]);
                        }
                        if (nodeData.title) {
                            node.title = nodeData.title;
                        }
                        if (nodeData.properties) {
                            Object.assign(node.properties, nodeData.properties);
                        }
                    }
                });

                if (canvasInstanceRef.current) {
                    canvasInstanceRef.current.draw(true, true);
                    console.log('Estado cargado con posiciones:', graphData.nodes.map(n => ({ id: n.id, pos: n.pos })));
                }
            }
        } catch (error) {
            console.error('Error loading graph state:', error);
        }
    }, []);

    const initializeGraph = useCallback(() => {
        if (!canvasRef.current) return;

        if (!graphRef.current) {
            graphRef.current = new LGraph();
        }

        canvasInstanceRef.current = new LGraphCanvas(canvasRef.current, graphRef.current);

        const graph = graphRef.current;
        const canvas = canvasInstanceRef.current;

        graph.onNodeAdded = saveGraphState;
        graph.onNodeRemoved = saveGraphState;
        graph.onConnectionChange = saveGraphState;
        graph.onNodePropertyChanged = saveGraphState;
        graph.onNodeTitleChanged = saveGraphState;

        canvas.onNodeMoved = () => {
            console.log('Nodo movido');
            saveGraphState();
        };

        canvas.onMouseUp = () => {
            console.log('Mouse liberado');
            saveGraphState();
        };

        canvas.onShowNodePanel = () => setTimeout(saveGraphState, 100);

        console.log('Graph initialized');
        loadGraphState();
        graph.start();

        canvas.draw(true, true);
    }, [loadGraphState, saveGraphState]);

    useEffect(() => {
        console.log('LiteGraphComponent rendered');
        initializeGraph();
        
        // Desactiva el guardado hasta que pase 1 segundo
        const timeoutId = setTimeout(() => setIsSaveEnabled(true), 1000);

        return () => {
            clearTimeout(timeoutId); // Limpia el timeout si el componente se desmonta
            if (graphRef.current) {
                graphRef.current.stop();
            }
        };
    }, [initializeGraph]);

    useEffect(() => {
        const handleResize = () => {
            if (canvasInstanceRef.current) {
                canvasInstanceRef.current.draw(true, true);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div>
            <canvas 
                ref={canvasRef} 
                width="1024" 
                height="720" 
                style={{ border: '1px solid' }} 
            />
        </div>
    );
};

export default LiteGraphComponent;
